import Image from '@tiptap/extension-image';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Markdown from 'markdown-to-jsx';
import React, { useCallback, useEffect, useRef } from 'react';
import { Markdown as TiptapMarkdown } from 'tiptap-markdown';

import FigureRenderer from '../components/FigureRenderer';
import { useEditContext, useIsEditing } from './EditContext';
import * as styles from './EditableMarkdown.css';
import FigureImage from './extensions/FigureImage';

type Props = {
  body: string;
  filePath: string;
  frontmatter: Record<string, string>;
  serializeFn: (frontmatter: Record<string, string>, body: string) => string;
  contentImageClassName?: string;
  children?: React.ReactNode;
};

// Pending image uploads: blob URL → { file, realPath (assigned after upload) }
export const pendingImages = new Map<string, { file: File; realPath?: string }>();

// After images are uploaded, maps blob URL → real path
const blobToPathMap = new Map<string, string>();

export function hasPendingImages(): boolean {
  return pendingImages.size > 0;
}

export function getPendingImageFiles(): Array<{ path: string; file: File }> {
  const result: Array<{ path: string; file: File }> = [];
  for (const [blobUrl, entry] of pendingImages) {
    const fileName = `${Date.now()}-${entry.file.name}`;
    const gitPath = `static/images/uploads/${fileName}`;
    blobToPathMap.set(blobUrl, `/images/uploads/${fileName}`);
    result.push({ path: gitPath, file: entry.file });
  }
  return result;
}

export function clearPendingImages(): void {
  for (const blobUrl of pendingImages.keys()) {
    URL.revokeObjectURL(blobUrl);
  }
  pendingImages.clear();
  blobToPathMap.clear();
}

function replaceBlobUrls(markdown: string): string {
  let result = markdown;
  for (const [blobUrl, realPath] of blobToPathMap) {
    result = result.replaceAll(blobUrl, realPath);
  }
  return result;
}

export function replaceBlobUrlsInString(content: string): string {
  return replaceBlobUrls(content);
}

function MenuBar({ editor, token }: { editor: any; token: string | null }) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleImageUpload = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;

      // Create blob URL for immediate in-editor preview
      const blobUrl = URL.createObjectURL(file);

      // Store file for deferred upload on save
      pendingImages.set(blobUrl, { file });

      // Insert as figureImage node with resize/caption support
      editor?.chain().focus().setFigureImage({ src: blobUrl, alt: file.name }).run();

      if (fileInputRef.current) fileInputRef.current.value = '';
    },
    [editor]
  );

  if (!editor) return null;

  const btn = (
    label: string,
    action: () => void,
    isActive?: boolean
  ) => (
    <button
      className={isActive ? styles.menuButtonActive : styles.menuButton}
      onClick={action}
      type="button"
    >
      {label}
    </button>
  );

  return (
    <div className={styles.menuBar}>
      {btn('B', () => editor.chain().focus().toggleBold().run(), editor.isActive('bold'))}
      {btn('I', () => editor.chain().focus().toggleItalic().run(), editor.isActive('italic'))}
      {btn('S', () => editor.chain().focus().toggleStrike().run(), editor.isActive('strike'))}
      <div className={styles.separator} />
      {btn('H1', () => editor.chain().focus().toggleHeading({ level: 1 }).run(), editor.isActive('heading', { level: 1 }))}
      {btn('H2', () => editor.chain().focus().toggleHeading({ level: 2 }).run(), editor.isActive('heading', { level: 2 }))}
      {btn('H3', () => editor.chain().focus().toggleHeading({ level: 3 }).run(), editor.isActive('heading', { level: 3 }))}
      <div className={styles.separator} />
      {btn('• List', () => editor.chain().focus().toggleBulletList().run(), editor.isActive('bulletList'))}
      {btn('1. List', () => editor.chain().focus().toggleOrderedList().run(), editor.isActive('orderedList'))}
      {btn('Quote', () => editor.chain().focus().toggleBlockquote().run(), editor.isActive('blockquote'))}
      <div className={styles.separator} />
      {btn('Link', () => {
        const url = window.prompt('URL:');
        if (url) editor.chain().focus().setLink({ href: url }).run();
      })}
      {btn('🖼 Image', () => fileInputRef.current?.click())}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className={styles.imageUploadInput}
        onChange={handleImageUpload}
      />
      <div className={styles.separator} />
      {btn('Undo', () => editor.chain().focus().undo().run())}
      {btn('Redo', () => editor.chain().focus().redo().run())}
    </div>
  );
}

export default function EditableMarkdown({
  body,
  filePath,
  frontmatter,
  serializeFn,
  contentImageClassName,
  children,
}: Props) {
  const isEditing = useIsEditing();
  const { registerChange, token } = useEditContext();

  const editor = useEditor({
    extensions: [
      StarterKit,
      Image.configure({ inline: false }),
      FigureImage,
      TiptapMarkdown.configure({
        html: true,
        transformCopiedText: true,
        transformPastedText: true,
      }),
    ],
    content: body,
    editorProps: {
      attributes: {
        class: 'tiptap',
      },
    },
    onCreate: ({ editor }) => {
      editor.commands.setContent(body);
    },
  });

  // Register changes on content update
  useEffect(() => {
    if (!editor || !isEditing) return;

    const handler = () => {
      let md = (editor.storage as any).markdown?.getMarkdown?.() || '';
      // Replace blob preview URLs with real GitHub paths
      md = replaceBlobUrls(md);
      const serialized = serializeFn(frontmatter, md);
      registerChange(filePath, {
        filePath,
        frontmatter,
        body: serialized,
      });
    };

    editor.on('update', handler);
    return () => {
      editor.off('update', handler);
    };
  }, [editor, isEditing, filePath, frontmatter, serializeFn, registerChange]);

  if (!isEditing) {
    if (children) return <>{children}</>;
    return (
      <Markdown
        options={{
          overrides: {
            figure: { component: FigureRenderer },
            ...(contentImageClassName
              ? {
                  img: {
                    component: ({ src, alt, title }: any) => (
                      <img
                        src={src}
                        alt={alt}
                        title={title}
                        className={contentImageClassName}
                      />
                    ),
                  },
                }
              : {}),
          },
        }}
      >
        {body}
      </Markdown>
    );
  }

  return (
    <div className={styles.editorWrapper}>
      <MenuBar editor={editor} token={token} />
      <EditorContent editor={editor} />
    </div>
  );
}
